import { useEffect, useMemo, useRef, useState } from "react";
import {
  CandlestickSeries,
  ColorType,
  HistogramSeries,
  LineSeries,
  LineStyle,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type Logical,
  type MouseEventParams,
  type Time,
} from "lightweight-charts";

import type { ChartBar } from "@/services/adapters/chart-adapter";

interface MarketChartProps {
  bars: ChartBar[];
  indicators: string[];
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  datasetKey?: string;
  onNeedMoreData?: (direction: "before" | "after", anchorTime: string) => void | Promise<void>;
}

type PaneKind = "main" | "volume" | "macd" | "kdj";
type PaneSeries = ISeriesApi<"Candlestick"> | ISeriesApi<"Histogram"> | ISeriesApi<"Line">;

interface PaneDefinition {
  id: PaneKind;
  label: string;
  weight: number;
}

interface NumericPoint {
  time: string;
  value: number;
}

interface OverlayData {
  ma5: NumericPoint[];
  ma10: NumericPoint[];
  ma20: NumericPoint[];
  bollUpper: NumericPoint[];
  bollMiddle: NumericPoint[];
  bollLower: NumericPoint[];
}

interface MacdData {
  dif: NumericPoint[];
  dea: NumericPoint[];
  histogram: NumericPoint[];
}

interface KdjData {
  k: NumericPoint[];
  d: NumericPoint[];
  j: NumericPoint[];
}

interface ChartSnapshot {
  bars: ChartBar[];
  indicators: string[];
  overlayData: OverlayData;
  macdData: MacdData;
  kdjData: KdjData;
}

interface PaneRuntime {
  id: PaneKind;
  container: HTMLDivElement;
  chart: IChartApi;
  primarySeries: PaneSeries;
  valueByTime: Map<string, number>;
  applyData: (snapshot: ChartSnapshot) => void;
  resize: () => void;
}

interface SizeState {
  width: number;
  height: number;
}

interface PaneDragState {
  index: number;
  startY: number;
  startRatios: number[];
  containerHeight: number;
}

interface SeriesShapeState {
  datasetKey?: string;
  firstTime?: string;
  lastTime?: string;
  length: number;
}

interface LatestInputs {
  bars: ChartBar[];
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  datasetKey?: string;
  onNeedMoreData?: (direction: "before" | "after", anchorTime: string) => void | Promise<void>;
}

const CHART_LAYOUT = {
  background: { type: ColorType.Solid, color: "#0f1115" },
  textColor: "#cfd6df",
  attributionLogo: false,
} as const;

const CHART_GRID = {
  horzLines: { color: "rgba(255, 255, 255, 0.05)" },
  vertLines: { color: "rgba(255, 255, 255, 0.04)" },
} as const;

const PRICE_SCALE = {
  borderColor: "rgba(255, 255, 255, 0.1)",
  minimumWidth: 64,
  scaleMargins: { top: 0.07, bottom: 0.08 },
} as const;

const SUB_PANE_SCALE = {
  borderColor: "rgba(255, 255, 255, 0.1)",
  minimumWidth: 64,
  scaleMargins: { top: 0.16, bottom: 0.12 },
} as const;

const TIME_SCALE_BASE = {
  borderColor: "rgba(255, 255, 255, 0.08)",
  rightOffset: 8,
  minBarSpacing: 0.4,
  fixLeftEdge: false,
  fixRightEdge: false,
  lockVisibleTimeRangeOnResize: true,
  timeVisible: true,
  secondsVisible: false,
} as const;

const RIGHT_SCALE_BASE_MIN_WIDTH = Math.max(PRICE_SCALE.minimumWidth ?? 64, SUB_PANE_SCALE.minimumWidth ?? 64);
const MIN_PANE_RATIO = 0.1;

const ZOOM_DEBUG_ENABLED = true;

function nowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function toDebugRange(range: { from: number; to: number } | null | undefined) {
  if (!range) return null;
  return {
    from: Number(range.from.toFixed(3)),
    to: Number(range.to.toFixed(3)),
    span: Number((range.to - range.from).toFixed(3)),
  };
}

function zoomDebugLog(event: string, payload: Record<string, unknown>) {
  if (!ZOOM_DEBUG_ENABLED || typeof window === "undefined") {
    return;
  }
  const entry = {
    ts: new Date().toISOString(),
    event,
    ...payload,
  };
  const debugWindow = window as unknown as { __lisaqZoomDebugEvents?: Array<Record<string, unknown>> };
  if (!Array.isArray(debugWindow.__lisaqZoomDebugEvents)) {
    debugWindow.__lisaqZoomDebugEvents = [];
  }
  debugWindow.__lisaqZoomDebugEvents.push(entry);
  if (debugWindow.__lisaqZoomDebugEvents.length > 1200) {
    debugWindow.__lisaqZoomDebugEvents.shift();
  }
  console.debug("[lisaq-zoom]", entry);
}

export function MarketChart({
  bars,
  indicators,
  hasMoreBefore,
  hasMoreAfter,
  datasetKey,
  onNeedMoreData,
}: MarketChartProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const paneRefs = useRef<Array<HTMLDivElement | null>>([]);
  const runtimesRef = useRef<PaneRuntime[]>([]);
  const syncingRangeRef = useRef(false);
  const syncingCrosshairRef = useRef(false);
  const pendingEdgeRequestRef = useRef<Set<string>>(new Set());
  const lastVisibleRangeRef = useRef<{ from: number; to: number } | null>(null);
  const lastVisibleRangeDatasetKeyRef = useRef<string | null>(null);
  const previousSeriesShapeRef = useRef<SeriesShapeState>({ length: 0 });
  const latestInputsRef = useRef<LatestInputs>({
    bars: [],
    hasMoreBefore: false,
    hasMoreAfter: false,
    datasetKey: undefined,
    onNeedMoreData: undefined,
  });
  const gutterSyncFrameRef = useRef<number | null>(null);
  const gutterShrinkTimerRef = useRef<number | null>(null);
  const lastPointerXRef = useRef<number | null>(null);
  const zoomTxnRef = useRef(0);
  const zoomApplyingUntilRef = useRef(0);
  const awaitingPointerResyncRef = useRef(false);
  const metaModifierActiveRef = useRef(false);
  const paneDragRef = useRef<PaneDragState | null>(null);
  const [size, setSize] = useState<SizeState>({ width: 0, height: 0 });

  const paneDefinitions = useMemo<PaneDefinition[]>(() => {
    const panes: PaneDefinition[] = [
      { id: "main", label: "主图", weight: 8 },
      { id: "volume", label: "VOL", weight: 2 },
    ];
    if (indicators.includes("macd")) {
      panes.push({ id: "macd", label: "MACD", weight: 2.2 });
    }
    if (indicators.includes("kdj")) {
      panes.push({ id: "kdj", label: "KDJ", weight: 2.2 });
    }
    return panes;
  }, [indicators]);

  const normalizedBars = useMemo(
    () =>
      bars.map((bar) => ({
        ...bar,
        time: normalizeTimeInput(bar.time),
      })),
    [bars],
  );
  const overlayData = useMemo(() => buildMainOverlayData(normalizedBars), [normalizedBars]);
  const macdData = useMemo(() => buildMacdData(normalizedBars), [normalizedBars]);
  const kdjData = useMemo(() => buildKdjData(normalizedBars), [normalizedBars]);
  const snapshot = useMemo<ChartSnapshot>(
    () => ({
      bars: normalizedBars,
      indicators,
      overlayData,
      macdData,
      kdjData,
    }),
    [indicators, kdjData, macdData, normalizedBars, overlayData],
  );
  const paneStructureKey = useMemo(
    () =>
      [
        paneDefinitions.map((pane) => pane.id).join("|"),
        indicators.includes("ma") ? "ma" : "no-ma",
        indicators.includes("boll") ? "boll" : "no-boll",
      ].join(";"),
    [indicators, paneDefinitions],
  );
  const defaultPaneRatios = useMemo(() => {
    const totalWeight = paneDefinitions.reduce((sum, pane) => sum + pane.weight, 0);
    return paneDefinitions.map((pane) => pane.weight / totalWeight);
  }, [paneDefinitions]);
  const [paneRatios, setPaneRatios] = useState<number[]>(defaultPaneRatios);
  const paneHeightStyles = useMemo(() => {
    const source = paneRatios.length === paneDefinitions.length ? paneRatios : defaultPaneRatios;
    const total = source.reduce((sum, ratio) => sum + ratio, 0) || 1;
    return source.map((ratio) => `${(ratio / total) * 100}%`);
  }, [defaultPaneRatios, paneDefinitions.length, paneRatios]);

  latestInputsRef.current = {
    bars: normalizedBars,
    hasMoreBefore,
    hasMoreAfter,
    datasetKey,
    onNeedMoreData,
  };

  useEffect(() => {
    setPaneRatios(defaultPaneRatios);
  }, [defaultPaneRatios]);

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      const drag = paneDragRef.current;
      if (!drag) {
        return;
      }
      event.preventDefault();
      const deltaRatio = (event.clientY - drag.startY) / Math.max(1, drag.containerHeight);
      const nextTop = drag.startRatios[drag.index] + deltaRatio;
      const nextBottom = drag.startRatios[drag.index + 1] - deltaRatio;
      if (nextTop < MIN_PANE_RATIO || nextBottom < MIN_PANE_RATIO) {
        return;
      }
      setPaneRatios(() => {
        const base = [...drag.startRatios];
        base[drag.index] = nextTop;
        base[drag.index + 1] = nextBottom;
        return base;
      });
    };
    const onMouseUp = () => {
      if (!paneDragRef.current) {
        return;
      }
      paneDragRef.current = null;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const requestInteractiveGutterSync = () => {
    if (!runtimesRef.current.length) {
      return;
    }
    schedulePaneGutterSync(runtimesRef.current, gutterSyncFrameRef, false);
    if (gutterShrinkTimerRef.current !== null) {
      window.clearTimeout(gutterShrinkTimerRef.current);
    }
    gutterShrinkTimerRef.current = window.setTimeout(() => {
      gutterShrinkTimerRef.current = null;
      if (!runtimesRef.current.length) {
        return;
      }
      schedulePaneGutterSync(runtimesRef.current, gutterSyncFrameRef, true);
    }, 180);
  };

  const requestFullGutterSync = (runtimes: PaneRuntime[]) => {
    if (!runtimes.length) {
      return;
    }
    schedulePaneGutterSync(runtimes, gutterSyncFrameRef, true);
  };

  useEffect(() => {
    pendingEdgeRequestRef.current.clear();
    awaitingPointerResyncRef.current = false;
    if (datasetKey && lastVisibleRangeDatasetKeyRef.current !== datasetKey) {
      lastVisibleRangeRef.current = null;
    }
    zoomDebugLog("dataset_switch", {
      datasetKey,
      lastVisibleRangeDatasetKey: lastVisibleRangeDatasetKeyRef.current,
      bars: latestInputsRef.current.bars.length,
    });
  }, [datasetKey]);

  useEffect(() => {
    return () => {
      if (gutterShrinkTimerRef.current !== null) {
        window.clearTimeout(gutterShrinkTimerRef.current);
        gutterShrinkTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }
    const element = rootRef.current;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const nextWidth = Math.floor(entry.contentRect.width);
      const nextHeight = Math.floor(entry.contentRect.height);
      setSize((prev) => {
        if (prev.width === nextWidth && prev.height === nextHeight) {
          return prev;
        }
        return { width: nextWidth, height: nextHeight };
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    paneRefs.current.length = paneDefinitions.length;
  }, [paneDefinitions.length]);

  useEffect(() => {
    const element = rootRef.current;
    if (!element) {
      return;
    }

    const updatePointerX = (event: MouseEvent | PointerEvent) => {
      const mainRuntime = getMainRuntime(runtimesRef.current);
      if (!mainRuntime) {
        return;
      }
      const metrics = getChartPlotMetrics(mainRuntime);
      const rect = mainRuntime.container.getBoundingClientRect();
      lastPointerXRef.current = clampNumber(event.clientX - rect.left, 0, metrics.plotWidth);
    };
    const clearPointer = () => {
      lastPointerXRef.current = null;
      awaitingPointerResyncRef.current = false;
      syncingCrosshairRef.current = true;
      runtimesRef.current.forEach((runtime) => runtime.chart.clearCrosshairPosition());
      syncingCrosshairRef.current = false;
      zoomDebugLog("pointer_leave", {});
    };

    element.addEventListener("mousemove", updatePointerX, { passive: true });
    element.addEventListener("pointermove", updatePointerX, { passive: true });
    element.addEventListener("mouseleave", clearPointer);
    element.addEventListener("pointerleave", clearPointer);
    return () => {
      element.removeEventListener("mousemove", updatePointerX);
      element.removeEventListener("pointermove", updatePointerX);
      element.removeEventListener("mouseleave", clearPointer);
      element.removeEventListener("pointerleave", clearPointer);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey) {
        metaModifierActiveRef.current = true;
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (!event.metaKey && !event.ctrlKey) {
        metaModifierActiveRef.current = false;
      }
    };
    const onBlur = () => {
      metaModifierActiveRef.current = false;
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    window.addEventListener("keyup", onKeyUp, { capture: true });
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true });
      window.removeEventListener("keyup", onKeyUp, { capture: true });
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  useEffect(() => {
    const element = rootRef.current;
    if (!element) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) < 1) {
        return;
      }
      const mainRuntime = getMainRuntime(runtimesRef.current);
      if (!mainRuntime) {
        return;
      }

      event.preventDefault();

      const currentRange = lastVisibleRangeRef.current || mainRuntime.chart.timeScale().getVisibleLogicalRange();
      if (!currentRange) {
        return;
      }

      const latest = latestInputsRef.current;
      const zoomTxn = ++zoomTxnRef.current;
      const metrics = getChartPlotMetrics(mainRuntime);
      const previousPointerX = lastPointerXRef.current;
      const modifierActive = event.metaKey || event.ctrlKey || metaModifierActiveRef.current;
      const anchor = resolveZoomAnchor({
        event,
        mainRuntime,
        currentRange,
        lastPointerX: previousPointerX,
        plotWidth: metrics.plotWidth,
        pointerZoomMode: modifierActive,
      });
      lastPointerXRef.current = anchor.pointX;
      zoomDebugLog("zoom_wheel_start", {
        zoomTxn,
        datasetKey: latest.datasetKey ?? null,
        bars: latest.bars.length,
        deltaY: event.deltaY,
        metaKey: event.metaKey,
        ctrlKey: event.ctrlKey,
        modifierActive,
        eventClientX: event.clientX,
        lastPointerX: previousPointerX,
        pointerFromEvent: anchor.pointerFromEvent,
        pointerSource: anchor.source,
        anchorPointX: anchor.pointX,
        anchorLogical: Number(anchor.logical.toFixed(3)),
        anchorRatio: Number(anchor.ratio.toFixed(4)),
        plotWidth: metrics.plotWidth,
        containerWidth: metrics.containerWidth,
        rightScaleWidth: metrics.rightScaleWidth,
        currentRange: toDebugRange(currentRange),
      });
      const nextRange = buildZoomedRange(currentRange, anchor, event.deltaY, latest.bars.length);
      zoomDebugLog("zoom_wheel_next_range", {
        zoomTxn,
        nextRange: toDebugRange(nextRange),
      });

      syncingRangeRef.current = true;
      syncingCrosshairRef.current = true;
      awaitingPointerResyncRef.current = true;
      zoomApplyingUntilRef.current = nowMs() + 220;
      applyVisibleRangeToAll(runtimesRef.current, nextRange);
      syncingRangeRef.current = false;
      syncingCrosshairRef.current = false;

      lastVisibleRangeRef.current = nextRange;
      lastVisibleRangeDatasetKeyRef.current = latest.datasetKey ?? null;

      maybeRequestEdgeBars({
        range: nextRange,
        bars: latest.bars,
        hasMoreAfter: latest.hasMoreAfter,
        hasMoreBefore: latest.hasMoreBefore,
        onNeedMoreData: latest.onNeedMoreData,
        requestRegistry: pendingEdgeRequestRef.current,
        series: mainRuntime.primarySeries,
      });
    };

    element.addEventListener("wheel", handleWheel, { passive: false });
    return () => element.removeEventListener("wheel", handleWheel);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.metaKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) {
        return;
      }
      if (isEditableTarget(event.target)) {
        return;
      }

      const mainRuntime = getMainRuntime(runtimesRef.current);
      if (!mainRuntime) {
        return;
      }

      event.preventDefault();

      const currentRange = lastVisibleRangeRef.current || mainRuntime.chart.timeScale().getVisibleLogicalRange();
      if (!currentRange) {
        return;
      }

      const latest = latestInputsRef.current;
      const zoomTxn = ++zoomTxnRef.current;
      const metrics = getChartPlotMetrics(mainRuntime);
      const anchor = resolveKeyboardZoomAnchor(mainRuntime, currentRange, lastPointerXRef.current, metrics.plotWidth);
      const deltaY = event.key === "ArrowUp" ? -120 : 120;
      zoomDebugLog("zoom_key_start", {
        zoomTxn,
        datasetKey: latest.datasetKey ?? null,
        bars: latest.bars.length,
        key: event.key,
        pointerSource: anchor.source,
        anchorPointX: anchor.pointX,
        anchorLogical: Number(anchor.logical.toFixed(3)),
        anchorRatio: Number(anchor.ratio.toFixed(4)),
        plotWidth: metrics.plotWidth,
        containerWidth: metrics.containerWidth,
        rightScaleWidth: metrics.rightScaleWidth,
        currentRange: toDebugRange(currentRange),
      });
      const nextRange = buildZoomedRange(currentRange, anchor, deltaY, latest.bars.length);
      zoomDebugLog("zoom_key_next_range", {
        zoomTxn,
        nextRange: toDebugRange(nextRange),
      });

      syncingRangeRef.current = true;
      syncingCrosshairRef.current = true;
      awaitingPointerResyncRef.current = true;
      zoomApplyingUntilRef.current = nowMs() + 220;
      applyVisibleRangeToAll(runtimesRef.current, nextRange);
      syncingRangeRef.current = false;
      syncingCrosshairRef.current = false;

      lastVisibleRangeRef.current = nextRange;
      lastVisibleRangeDatasetKeyRef.current = latest.datasetKey ?? null;

      maybeRequestEdgeBars({
        range: nextRange,
        bars: latest.bars,
        hasMoreAfter: latest.hasMoreAfter,
        hasMoreBefore: latest.hasMoreBefore,
        onNeedMoreData: latest.onNeedMoreData,
        requestRegistry: pendingEdgeRequestRef.current,
        series: mainRuntime.primarySeries,
      });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const containers = paneRefs.current.slice(0, paneDefinitions.length);
    if (!containers.length || containers.some((container) => !container)) {
      return;
    }

    const runtimes = paneDefinitions.map((pane, index) =>
      createPaneRuntime({
        pane,
        container: containers[index] as HTMLDivElement,
        indicators,
        snapshot,
      }),
    );

    const rangeHandlers = runtimes.map((runtime) => {
      const handler = (range: { from: number; to: number } | null) => {
        if (!range || syncingRangeRef.current) {
          return;
        }
        zoomDebugLog("range_change", {
          sourcePane: runtime.id,
          range: toDebugRange(range),
          datasetKey: latestInputsRef.current.datasetKey ?? null,
        });
        lastVisibleRangeRef.current = range;
        lastVisibleRangeDatasetKeyRef.current = latestInputsRef.current.datasetKey ?? null;

        syncingRangeRef.current = true;
        runtimesRef.current.forEach((target) => {
          if (target.id !== runtime.id) {
            target.chart.timeScale().setVisibleLogicalRange(range);
          }
        });
        syncingRangeRef.current = false;
        requestInteractiveGutterSync();

        const latest = latestInputsRef.current;
        maybeRequestEdgeBars({
          range,
          bars: latest.bars,
          hasMoreAfter: latest.hasMoreAfter,
          hasMoreBefore: latest.hasMoreBefore,
          onNeedMoreData: latest.onNeedMoreData,
          requestRegistry: pendingEdgeRequestRef.current,
          series: getMainRuntime(runtimesRef.current)?.primarySeries || runtime.primarySeries,
        });
      };
      runtime.chart.timeScale().subscribeVisibleLogicalRangeChange(handler);
      return { runtime, handler };
    });

    const crosshairHandlers = runtimes.map((runtime) => {
      const handler = (param: MouseEventParams<Time>) => {
        if (syncingCrosshairRef.current) {
          return;
        }
        const hasSourceEvent = Boolean(param.sourceEvent);
        const duringZoomApply = nowMs() < zoomApplyingUntilRef.current;
        const waitingForPointerResync = awaitingPointerResyncRef.current;

        if ((!param.point || !param.time) && !hasSourceEvent) {
          zoomDebugLog("crosshair_clear_ignored_no_source", {
            sourcePane: runtime.id,
            duringZoomApply,
            waitingForPointerResync,
          });
          return;
        }
        if (
          (!param.point || !param.time) &&
          (duringZoomApply || (waitingForPointerResync && !hasSourceEvent))
        ) {
          zoomDebugLog("crosshair_clear_ignored", {
            sourcePane: runtime.id,
            duringZoomApply,
            waitingForPointerResync,
            hasSourceEvent,
          });
          return;
        }
        if (duringZoomApply && !hasSourceEvent) {
          zoomDebugLog("crosshair_ignored_during_zoom", {
            sourcePane: runtime.id,
            hasPoint: Boolean(param.point),
            hasTime: Boolean(param.time),
            hasSourceEvent,
          });
          return;
        }
        if (waitingForPointerResync && !hasSourceEvent) {
          zoomDebugLog("crosshair_ignored_awaiting_pointer", {
            sourcePane: runtime.id,
            hasPoint: Boolean(param.point),
            hasTime: Boolean(param.time),
            hasSourceEvent,
          });
          return;
        }
        if (param.point && param.time && !hasSourceEvent) {
          zoomDebugLog("crosshair_ignored_no_source_move", {
            sourcePane: runtime.id,
            pointX: Number(param.point.x.toFixed(2)),
            timeKey: stringifyTime(param.time),
          });
          return;
        }

        syncingCrosshairRef.current = true;
        try {
          if (!param.point || !param.time) {
            zoomDebugLog("crosshair_clear", {
              sourcePane: runtime.id,
              hasSourceEvent,
            });
            if (hasSourceEvent) {
              awaitingPointerResyncRef.current = false;
            }
            runtimesRef.current.forEach((target) => {
              if (target.id !== runtime.id) {
                target.chart.clearCrosshairPosition();
              }
            });
            return;
          }

          if (hasSourceEvent) {
            const metrics = getChartPlotMetrics(runtime);
            const sourceLocalX = param.sourceEvent?.localX;
            const pointerX = clampNumber(
              typeof sourceLocalX === "number" ? sourceLocalX : param.point.x,
              0,
              metrics.plotWidth,
            );
            if (awaitingPointerResyncRef.current) {
              zoomDebugLog("pointer_resynced", {
                sourcePane: runtime.id,
                pointerX: Number(pointerX.toFixed(2)),
              });
            }
            lastPointerXRef.current = pointerX;
            awaitingPointerResyncRef.current = false;
          }

          const timeKey = stringifyTime(param.time);
          zoomDebugLog("crosshair_move", {
            sourcePane: runtime.id,
            timeKey,
            pointX: Number(param.point.x.toFixed(2)),
            hasSourceEvent,
          });
          runtimesRef.current.forEach((target) => {
            if (target.id === runtime.id) {
              return;
            }
            const price = target.valueByTime.get(timeKey);
            if (price == null || !Number.isFinite(price)) {
              target.chart.clearCrosshairPosition();
              return;
            }
            target.chart.setCrosshairPosition(price, param.time as Time, target.primarySeries);
          });
        } finally {
          syncingCrosshairRef.current = false;
        }
      };
      runtime.chart.subscribeCrosshairMove(handler);
      return { runtime, handler };
    });

    runtimesRef.current = runtimes;
    runtimes.forEach((runtime) => runtime.resize());
    requestFullGutterSync(runtimes);
    const initializedRange = initializeVisibleRange(
      runtimes,
      latestInputsRef.current.datasetKey,
      latestInputsRef.current.bars.length,
      lastVisibleRangeRef.current,
      lastVisibleRangeDatasetKeyRef.current,
    );
    if (initializedRange) {
      lastVisibleRangeRef.current = initializedRange;
      lastVisibleRangeDatasetKeyRef.current = latestInputsRef.current.datasetKey ?? null;
    }

    zoomDebugLog("chart_created", {
      paneStructureKey,
      panes: runtimes.map((runtime) => runtime.id),
      bars: latestInputsRef.current.bars.length,
      size,
    });

    return () => {
      rangeHandlers.forEach(({ runtime, handler }) => runtime.chart.timeScale().unsubscribeVisibleLogicalRangeChange(handler));
      crosshairHandlers.forEach(({ runtime, handler }) => runtime.chart.unsubscribeCrosshairMove(handler));
      if (gutterSyncFrameRef.current !== null) {
        window.cancelAnimationFrame(gutterSyncFrameRef.current);
        gutterSyncFrameRef.current = null;
      }
      if (gutterShrinkTimerRef.current !== null) {
        window.clearTimeout(gutterShrinkTimerRef.current);
        gutterShrinkTimerRef.current = null;
      }
      zoomDebugLog("chart_disposed", {
        paneStructureKey,
        panes: runtimes.map((runtime) => runtime.id),
      });
      destroyCharts(runtimes);
      runtimesRef.current = [];
    };
  }, [paneStructureKey]);

  useEffect(() => {
    const runtimes = runtimesRef.current;
    if (!runtimes.length || !size.width || !size.height) {
      return;
    }

    runtimes.forEach((runtime) => runtime.resize());
    requestFullGutterSync(runtimes);

    const latest = latestInputsRef.current;
    if (latest.datasetKey && lastVisibleRangeDatasetKeyRef.current === latest.datasetKey && lastVisibleRangeRef.current) {
      const clampedRange = clampLogicalRange(lastVisibleRangeRef.current, latest.bars.length);
      syncingRangeRef.current = true;
      applyVisibleRangeToAll(runtimes, clampedRange);
      syncingRangeRef.current = false;
      lastVisibleRangeRef.current = clampedRange;
    }

    zoomDebugLog("size_effect", {
      size,
      bars: latest.bars.length,
      datasetKey: latest.datasetKey ?? null,
      restoredRange: latest.datasetKey && lastVisibleRangeDatasetKeyRef.current === latest.datasetKey && lastVisibleRangeRef.current
        ? toDebugRange(lastVisibleRangeRef.current)
        : null,
    });
  }, [size.height, size.width]);

  useEffect(() => {
    const runtimes = runtimesRef.current;
    if (!runtimes.length) {
      return;
    }
    runtimes.forEach((runtime) => runtime.resize());
    requestFullGutterSync(runtimes);
  }, [paneHeightStyles]);

  useEffect(() => {
    const runtimes = runtimesRef.current;
    if (!runtimes.length) {
      previousSeriesShapeRef.current = buildSeriesShape(datasetKey, normalizedBars);
      return;
    }

    const previousShape = previousSeriesShapeRef.current;
    const nextRange = resolveRangeAfterDataChange({
      previousShape,
      datasetKey,
      bars: normalizedBars,
      lastVisibleRange: lastVisibleRangeRef.current,
    });

    runtimes.forEach((runtime) => runtime.applyData(snapshot));
    requestFullGutterSync(runtimes);

    if (nextRange) {
      syncingRangeRef.current = true;
      applyVisibleRangeToAll(runtimes, nextRange);
      syncingRangeRef.current = false;
      lastVisibleRangeRef.current = nextRange;
      lastVisibleRangeDatasetKeyRef.current = datasetKey ?? null;
      zoomDebugLog("data_effect_apply_preserved_range", {
        datasetKey: datasetKey ?? null,
        range: toDebugRange(nextRange),
      });
    } else if (previousShape.datasetKey !== datasetKey) {
      const mainRuntime = getMainRuntime(runtimes) || runtimes[0];
      mainRuntime.chart.timeScale().fitContent();
      const fittedRange = mainRuntime.chart.timeScale().getVisibleLogicalRange();
      if (fittedRange) {
        syncingRangeRef.current = true;
        applyVisibleRangeToAll(runtimes, fittedRange);
        syncingRangeRef.current = false;
        lastVisibleRangeRef.current = fittedRange;
        lastVisibleRangeDatasetKeyRef.current = datasetKey ?? null;
        zoomDebugLog("data_effect_fit_content", {
          datasetKey: datasetKey ?? null,
          previousDatasetKey: previousShape.datasetKey ?? null,
          fittedRange: toDebugRange(fittedRange),
        });
      }
    }

    zoomDebugLog("data_effect", {
      datasetKey: datasetKey ?? null,
      previousDatasetKey: previousShape.datasetKey ?? null,
      bars: normalizedBars.length,
      nextRange: toDebugRange(nextRange),
    });

    previousSeriesShapeRef.current = buildSeriesShape(datasetKey, normalizedBars);
  }, [datasetKey, normalizedBars, snapshot]);

  return (
    <div ref={rootRef} className="relative flex h-full min-h-0 flex-col bg-[#0f1115]">
      {paneDefinitions.map((pane, index) => (
        <div
          key={pane.id}
          className={`relative min-h-0 border-[#1f232b] ${index === 0 ? "" : "border-t"}`}
          style={{ height: paneHeightStyles[index] }}
        >
          <div className="pointer-events-none absolute left-3 top-2 z-10 rounded bg-black/45 px-2 py-0.5 text-[10px] font-medium tracking-wide text-slate-400">
            {pane.label}
          </div>
          <div
            ref={(element) => {
              paneRefs.current[index] = element;
            }}
            className="h-full w-full"
          />
          {index < paneDefinitions.length - 1 ? (
            <button
              className="absolute inset-x-0 -bottom-1 z-20 h-2 cursor-row-resize bg-transparent touch-none"
              onMouseDown={(event) => {
                if (!rootRef.current) {
                  return;
                }
                event.preventDefault();
                const containerHeight = rootRef.current.getBoundingClientRect().height;
                paneDragRef.current = {
                  index,
                  startY: event.clientY,
                  startRatios: paneRatios.length === paneDefinitions.length ? paneRatios.slice() : defaultPaneRatios.slice(),
                  containerHeight: Math.max(1, containerHeight),
                };
                document.body.style.userSelect = "none";
                document.body.style.cursor = "row-resize";
              }}
              aria-label={`调整${paneDefinitions[index].label}与${paneDefinitions[index + 1].label}高度`}
            >
              <span className="pointer-events-none absolute inset-x-6 top-1/2 h-px -translate-y-1/2 bg-white/20" />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function createPaneRuntime({
  pane,
  container,
  indicators,
  snapshot,
}: {
  pane: PaneDefinition;
  container: HTMLDivElement;
  indicators: string[];
  snapshot: ChartSnapshot;
}): PaneRuntime {
  const chart = createChart(container, {
    layout: CHART_LAYOUT,
    grid: CHART_GRID,
    width: Math.max(container.clientWidth, 32),
    height: Math.max(container.clientHeight, 64),
    localization: { locale: "zh-CN" },
    rightPriceScale: pane.id === "main" ? PRICE_SCALE : SUB_PANE_SCALE,
    leftPriceScale: { visible: false },
    timeScale: {
      ...TIME_SCALE_BASE,
      visible: pane.id === getBottomPaneId(snapshot.indicators),
      ticksVisible: pane.id === getBottomPaneId(snapshot.indicators),
    },
    crosshair: {
      vertLine: {
        color: "rgba(125, 211, 252, 0.5)",
        labelBackgroundColor: "#0b6bcb",
        width: 1,
        style: LineStyle.Solid,
      },
      horzLine: {
        color: "rgba(255, 255, 255, 0.22)",
        labelBackgroundColor: "#1f2937",
        width: 1,
        style: LineStyle.Dashed,
      },
    },
    handleScroll: { mouseWheel: false, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
    handleScale: { axisPressedMouseMove: true, mouseWheel: false, pinch: false },
  });

  if (pane.id === "main") {
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#f6465d",
      downColor: "#16c784",
      wickUpColor: "#f6465d",
      wickDownColor: "#16c784",
      borderVisible: false,
      priceLineVisible: true,
      lastValueVisible: true,
    });
    const ma5Series = indicators.includes("ma") ? addLineSeries(chart, "#f59e0b", { lineWidth: 1 }) : null;
    const ma10Series = indicators.includes("ma") ? addLineSeries(chart, "#60a5fa", { lineWidth: 1 }) : null;
    const ma20Series = indicators.includes("ma") ? addLineSeries(chart, "#a78bfa", { lineWidth: 1 }) : null;
    const bollUpperSeries = indicators.includes("boll")
      ? addLineSeries(chart, "#38bdf8", { lineStyle: LineStyle.Dashed, lineWidth: 1 })
      : null;
    const bollMiddleSeries = indicators.includes("boll") ? addLineSeries(chart, "#94a3b8", { lineWidth: 1 }) : null;
    const bollLowerSeries = indicators.includes("boll")
      ? addLineSeries(chart, "#38bdf8", { lineStyle: LineStyle.Dashed, lineWidth: 1 })
      : null;
    const runtime: PaneRuntime = {
      id: pane.id,
      container,
      chart,
      primarySeries: candleSeries,
      valueByTime: new Map<string, number>(),
      applyData(nextSnapshot) {
        const nextBars = nextSnapshot.bars;
        candleSeries.setData(
          nextBars.map((bar) => ({
            time: bar.time,
            open: bar.open,
            high: bar.high,
            low: bar.low,
            close: bar.close,
          })),
        );
        ma5Series?.setData(nextSnapshot.overlayData.ma5);
        ma10Series?.setData(nextSnapshot.overlayData.ma10);
        ma20Series?.setData(nextSnapshot.overlayData.ma20);
        bollUpperSeries?.setData(nextSnapshot.overlayData.bollUpper);
        bollMiddleSeries?.setData(nextSnapshot.overlayData.bollMiddle);
        bollLowerSeries?.setData(nextSnapshot.overlayData.bollLower);
        runtime.valueByTime = new Map(nextBars.map((bar) => [bar.time, bar.close]));
      },
      resize() {
        chart.resize(Math.max(container.clientWidth, 32), Math.max(container.clientHeight, 64));
      },
    };
    runtime.applyData(snapshot);
    return runtime;
  }

  if (pane.id === "volume") {
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      lastValueVisible: true,
      priceLineVisible: false,
    });
    const runtime: PaneRuntime = {
      id: pane.id,
      container,
      chart,
      primarySeries: volumeSeries,
      valueByTime: new Map<string, number>(),
      applyData(nextSnapshot) {
        volumeSeries.setData(
          nextSnapshot.bars.map((bar) => ({
            time: bar.time,
            value: bar.volume,
            color: bar.close >= bar.open ? "rgba(246, 70, 93, 0.78)" : "rgba(22, 199, 132, 0.78)",
          })),
        );
        runtime.valueByTime = new Map(nextSnapshot.bars.map((bar) => [bar.time, bar.volume]));
      },
      resize() {
        chart.resize(Math.max(container.clientWidth, 32), Math.max(container.clientHeight, 64));
      },
    };
    runtime.applyData(snapshot);
    return runtime;
  }

  if (pane.id === "macd") {
    const histogramSeries = chart.addSeries(HistogramSeries, {
      priceLineVisible: false,
      lastValueVisible: true,
    });
    const difSeries = addLineSeries(chart, "#fbbf24", { lineWidth: 2 });
    const deaSeries = addLineSeries(chart, "#60a5fa", { lineWidth: 1 });
    const runtime: PaneRuntime = {
      id: pane.id,
      container,
      chart,
      primarySeries: difSeries,
      valueByTime: new Map<string, number>(),
      applyData(nextSnapshot) {
        histogramSeries.setData(
          nextSnapshot.macdData.histogram.map((point) => ({
            time: point.time,
            value: point.value,
            color: point.value >= 0 ? "rgba(246, 70, 93, 0.86)" : "rgba(22, 199, 132, 0.86)",
          })),
        );
        difSeries.setData(nextSnapshot.macdData.dif);
        deaSeries.setData(nextSnapshot.macdData.dea);
        runtime.valueByTime = toPointMap(nextSnapshot.macdData.dif);
      },
      resize() {
        chart.resize(Math.max(container.clientWidth, 32), Math.max(container.clientHeight, 64));
      },
    };
    runtime.applyData(snapshot);
    return runtime;
  }

  const kSeries = addLineSeries(chart, "#fbbf24", { lineWidth: 2 });
  const dSeries = addLineSeries(chart, "#60a5fa", { lineWidth: 1 });
  const jSeries = addLineSeries(chart, "#c084fc", { lineWidth: 1 });
  const runtime: PaneRuntime = {
    id: pane.id,
    container,
    chart,
    primarySeries: kSeries,
    valueByTime: new Map<string, number>(),
    applyData(nextSnapshot) {
      kSeries.setData(nextSnapshot.kdjData.k);
      dSeries.setData(nextSnapshot.kdjData.d);
      jSeries.setData(nextSnapshot.kdjData.j);
      runtime.valueByTime = toPointMap(nextSnapshot.kdjData.k);
    },
    resize() {
      chart.resize(Math.max(container.clientWidth, 32), Math.max(container.clientHeight, 64));
    },
  };
  runtime.applyData(snapshot);
  return runtime;
}

function addLineSeries(
  chart: IChartApi,
  color: string,
  options?: Partial<Parameters<IChartApi["addSeries"]>[1]>,
) {
  return chart.addSeries(LineSeries, {
    color,
    lineWidth: 1,
    lastValueVisible: true,
    crosshairMarkerVisible: false,
    ...options,
  });
}

function buildMainOverlayData(bars: ChartBar[]): OverlayData {
  return {
    ma5: calculateMovingAverage(bars, 5),
    ma10: calculateMovingAverage(bars, 10),
    ma20: calculateMovingAverage(bars, 20),
    ...calculateBollingerBands(bars, 20, 2),
  };
}

function buildMacdData(bars: ChartBar[]): MacdData {
  const ema12 = calculateEmaSeries(bars, 12);
  const ema26 = calculateEmaSeries(bars, 26);
  const difRaw = bars.map((bar, index) => ({
    time: bar.time,
    value: ema12[index].value - ema26[index].value,
  }));
  const deaRaw = calculateSignalSeries(difRaw, 9);
  const histogram = difRaw.map((point, index) => ({
    time: point.time,
    value: (point.value - deaRaw[index].value) * 2,
  }));
  return {
    dif: difRaw,
    dea: deaRaw,
    histogram,
  };
}

function buildKdjData(bars: ChartBar[]): KdjData {
  let prevK = 50;
  let prevD = 50;
  const k: NumericPoint[] = [];
  const d: NumericPoint[] = [];
  const j: NumericPoint[] = [];

  bars.forEach((bar, index) => {
    const windowBars = bars.slice(Math.max(0, index - 8), index + 1);
    const highest = Math.max(...windowBars.map((item) => item.high));
    const lowest = Math.min(...windowBars.map((item) => item.low));
    const denominator = highest - lowest;
    const rsv = denominator === 0 ? 50 : ((bar.close - lowest) / denominator) * 100;
    const nextK = prevK * (2 / 3) + rsv / 3;
    const nextD = prevD * (2 / 3) + nextK / 3;
    const nextJ = 3 * nextK - 2 * nextD;
    prevK = nextK;
    prevD = nextD;
    k.push({ time: bar.time, value: nextK });
    d.push({ time: bar.time, value: nextD });
    j.push({ time: bar.time, value: nextJ });
  });

  return { k, d, j };
}

function calculateMovingAverage(bars: ChartBar[], period: number) {
  return bars
    .map((bar, index) => {
      if (index + 1 < period) {
        return null;
      }
      const windowBars = bars.slice(index + 1 - period, index + 1);
      const average = windowBars.reduce((sum, item) => sum + item.close, 0) / period;
      return { time: bar.time, value: average };
    })
    .filter((item): item is NumericPoint => item !== null);
}

function calculateBollingerBands(bars: ChartBar[], period: number, multiplier: number) {
  const upper: NumericPoint[] = [];
  const middle: NumericPoint[] = [];
  const lower: NumericPoint[] = [];

  bars.forEach((bar, index) => {
    if (index + 1 < period) {
      return;
    }
    const windowBars = bars.slice(index + 1 - period, index + 1);
    const mean = windowBars.reduce((sum, item) => sum + item.close, 0) / period;
    const variance = windowBars.reduce((sum, item) => sum + (item.close - mean) ** 2, 0) / period;
    const deviation = Math.sqrt(variance);
    upper.push({ time: bar.time, value: mean + deviation * multiplier });
    middle.push({ time: bar.time, value: mean });
    lower.push({ time: bar.time, value: mean - deviation * multiplier });
  });

  return {
    bollUpper: upper,
    bollMiddle: middle,
    bollLower: lower,
  };
}

function calculateEmaSeries(bars: ChartBar[], period: number) {
  const multiplier = 2 / (period + 1);
  let previous = bars[0]?.close ?? 0;
  return bars.map((bar, index) => {
    const value = index === 0 ? bar.close : bar.close * multiplier + previous * (1 - multiplier);
    previous = value;
    return { time: bar.time, value };
  });
}

function calculateSignalSeries(points: NumericPoint[], period: number) {
  const multiplier = 2 / (period + 1);
  let previous = points[0]?.value ?? 0;
  return points.map((point, index) => {
    const value = index === 0 ? point.value : point.value * multiplier + previous * (1 - multiplier);
    previous = value;
    return { time: point.time, value };
  });
}

function normalizeTimeInput(raw: string) {
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  return raw;
}

function stringifyTime(time: unknown) {
  if (typeof time === "string") {
    return time;
  }
  if (time && typeof time === "object" && "year" in time && "month" in time && "day" in time) {
    const candidate = time as { year: number; month: number; day: number };
    const month = String(candidate.month).padStart(2, "0");
    const day = String(candidate.day).padStart(2, "0");
    return `${candidate.year}-${month}-${day}`;
  }
  return String(time ?? "");
}

function toPointMap(points: NumericPoint[]) {
  return new Map(points.map((point) => [point.time, point.value]));
}

function getMainRuntime(runtimes: PaneRuntime[]) {
  return runtimes.find((runtime) => runtime.id === "main") || null;
}

function getBottomPaneId(indicators: string[]) {
  if (indicators.includes("kdj")) {
    return "kdj";
  }
  if (indicators.includes("macd")) {
    return "macd";
  }
  return "volume";
}

function initializeVisibleRange(
  runtimes: PaneRuntime[],
  datasetKey: string | undefined,
  barCount: number,
  lastVisibleRange: { from: number; to: number } | null,
  lastVisibleRangeDatasetKey: string | null,
) {
  if (!runtimes.length) {
    return null;
  }
  const shouldRestore = datasetKey && lastVisibleRangeDatasetKey === datasetKey && lastVisibleRange !== null;
  if (shouldRestore) {
    const restoreRange = clampLogicalRange(lastVisibleRange, barCount);
    applyVisibleRangeToAll(runtimes, restoreRange);
    return restoreRange;
  }
  const mainRuntime = getMainRuntime(runtimes) || runtimes[0];
  mainRuntime.chart.timeScale().fitContent();
  const range = mainRuntime.chart.timeScale().getVisibleLogicalRange();
  if (range) {
    applyVisibleRangeToAll(runtimes, range);
    return range;
  }
  return null;
}

function resolveRangeAfterDataChange({
  previousShape,
  datasetKey,
  bars,
  lastVisibleRange,
}: {
  previousShape: SeriesShapeState;
  datasetKey?: string;
  bars: ChartBar[];
  lastVisibleRange: { from: number; to: number } | null;
}) {
  if (!datasetKey || previousShape.datasetKey !== datasetKey || !lastVisibleRange || previousShape.length === 0) {
    return null;
  }

  let barsAddedBefore = 0;
  if (previousShape.firstTime) {
    barsAddedBefore = bars.findIndex((bar) => bar.time === previousShape.firstTime);
    if (barsAddedBefore < 0) {
      barsAddedBefore = 0;
    }
  }

  return clampLogicalRange(
    {
      from: lastVisibleRange.from + barsAddedBefore,
      to: lastVisibleRange.to + barsAddedBefore,
    },
    bars.length,
  );
}

function buildSeriesShape(datasetKey: string | undefined, bars: ChartBar[]): SeriesShapeState {
  return {
    datasetKey,
    firstTime: bars[0]?.time,
    lastTime: bars[bars.length - 1]?.time,
    length: bars.length,
  };
}

function maybeRequestEdgeBars({
  range,
  bars,
  hasMoreAfter,
  hasMoreBefore,
  onNeedMoreData,
  requestRegistry,
  series,
}: {
  range: { from: number; to: number };
  bars: ChartBar[];
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  onNeedMoreData?: (direction: "before" | "after", anchorTime: string) => void | Promise<void>;
  requestRegistry: Set<string>;
  series: PaneSeries;
}) {
  if (!bars.length || !onNeedMoreData) {
    return;
  }
  const barsInfo = series.barsInLogicalRange(range);
  if (!barsInfo) {
    return;
  }

  const nearLeftEdge = (barsInfo.barsBefore ?? Number.POSITIVE_INFINITY) < 10 || range.from < 5;
  const nearRightEdge = (barsInfo.barsAfter ?? Number.POSITIVE_INFINITY) < 10 || range.to > bars.length - 5;

  if (hasMoreBefore && nearLeftEdge) {
    const anchor = bars[0]?.time;
    if (anchor) {
      const key = `before:${anchor}`;
      if (!requestRegistry.has(key)) {
        requestRegistry.add(key);
        zoomDebugLog("edge_request_start", {
          direction: "before",
          anchorTime: anchor,
          key,
          range: toDebugRange(range),
          barsBefore: barsInfo.barsBefore ?? null,
          barsAfter: barsInfo.barsAfter ?? null,
        });
        void onNeedMoreData("before", anchor);
      } else {
        zoomDebugLog("edge_request_skip_pending", {
          direction: "before",
          anchorTime: anchor,
          key,
          range: toDebugRange(range),
        });
      }
    }
  }

  if (hasMoreAfter && nearRightEdge) {
    const anchor = bars[bars.length - 1]?.time;
    if (anchor) {
      const key = `after:${anchor}`;
      if (!requestRegistry.has(key)) {
        requestRegistry.add(key);
        zoomDebugLog("edge_request_start", {
          direction: "after",
          anchorTime: anchor,
          key,
          range: toDebugRange(range),
          barsBefore: barsInfo.barsBefore ?? null,
          barsAfter: barsInfo.barsAfter ?? null,
        });
        void onNeedMoreData("after", anchor);
      } else {
        zoomDebugLog("edge_request_skip_pending", {
          direction: "after",
          anchorTime: anchor,
          key,
          range: toDebugRange(range),
        });
      }
    }
  }
}

function destroyCharts(runtimes: PaneRuntime[]) {
  runtimes.forEach((runtime) => runtime.chart.remove());
}

function syncPaneGutters(runtimes: PaneRuntime[], allowShrink: boolean) {
  if (!runtimes.length) {
    return;
  }
  if (allowShrink) {
    // Release all panes to base width first so gutters can shrink when text becomes shorter.
    runtimes.forEach((runtime) => {
      runtime.chart.priceScale("right").applyOptions({ minimumWidth: RIGHT_SCALE_BASE_MIN_WIDTH });
    });
  }

  // Measure each pane's required width and align all panes to the global max.
  const measuredWidths = runtimes.map((runtime) => runtime.chart.priceScale("right").width());
  const maxRightScaleWidth = Math.max(...measuredWidths, RIGHT_SCALE_BASE_MIN_WIDTH);
  runtimes.forEach((runtime) => {
    runtime.chart.priceScale("right").applyOptions({ minimumWidth: maxRightScaleWidth });
  });
  zoomDebugLog("gutter_sync", {
    allowShrink,
    widths: measuredWidths,
    maxRightScaleWidth,
    panes: runtimes.map((runtime) => runtime.id),
  });
}

function schedulePaneGutterSync(
  runtimes: PaneRuntime[],
  frameRef: { current: number | null },
  allowShrink: boolean,
) {
  const run = (attemptsLeft: number) => {
    syncPaneGutters(runtimes, allowShrink);
    if (attemptsLeft <= 0) {
      frameRef.current = null;
      return;
    }
    frameRef.current = window.requestAnimationFrame(() => run(attemptsLeft - 1));
  };
  if (frameRef.current !== null) {
    window.cancelAnimationFrame(frameRef.current);
  }
  const attempts = allowShrink ? 2 : 1;
  frameRef.current = window.requestAnimationFrame(() => run(attempts));
}

function applyVisibleRangeToAll(runtimes: PaneRuntime[], range: { from: number; to: number }) {
  runtimes.forEach((runtime) => runtime.chart.timeScale().setVisibleLogicalRange(range));
}

function clampLogicalRange(range: { from: number; to: number }, barCount: number) {
  if (barCount <= 0) {
    return range;
  }
  const maxIndex = Math.max(0, barCount - 1);
  const span = Math.max(10, range.to - range.from);
  let to = Math.min(maxIndex + 50, Math.max(range.to, 0));
  let from = to - span;
  if (from < -50) {
    from = -50;
    to = from + span;
  }
  return { from, to };
}

function getChartPlotMetrics(runtime: PaneRuntime) {
  const containerWidth = Math.max(1, runtime.container.clientWidth);
  const rightScaleWidth = Math.max(0, runtime.chart.priceScale("right").width());
  const plotWidth = Math.max(1, containerWidth - rightScaleWidth);
  return {
    containerWidth,
    rightScaleWidth,
    plotWidth,
  };
}

function resolveZoomAnchor({
  event,
  mainRuntime,
  currentRange,
  lastPointerX,
  plotWidth,
  pointerZoomMode,
}: {
  event: WheelEvent;
  mainRuntime: PaneRuntime;
  currentRange: { from: number; to: number };
  lastPointerX: number | null;
  plotWidth: number;
  pointerZoomMode: boolean;
}) {
  const timeScale = mainRuntime.chart.timeScale();
  const rect = mainRuntime.container.getBoundingClientRect();
  const rawPointerFromEvent = event.clientX - rect.left;
  const pointerFromEvent = clampNumber(rawPointerFromEvent, 0, plotWidth);
  const hasReliableEventPointer =
    Number.isFinite(rawPointerFromEvent) &&
    event.clientX > 0 &&
    rawPointerFromEvent >= -2 &&
    rawPointerFromEvent <= plotWidth + 2;
  const fallbackPointer = clampNumber(lastPointerX ?? plotWidth, 0, plotWidth);
  const pointX = hasReliableEventPointer ? pointerFromEvent : fallbackPointer;
  const source = hasReliableEventPointer ? "wheel-event" : lastPointerX == null ? "right-edge" : "last-pointer";

  if (!pointerZoomMode) {
    return {
      logical: currentRange.to,
      ratio: 1,
      pointX,
      source,
      pointerFromEvent: Number(pointerFromEvent.toFixed(2)),
    };
  }

  // Command + wheel must zoom around the current pointer position.
  const anchorCoordinate = clampNumber(pointX, 0, plotWidth);
  const anchorLogicalFromCoord = timeScale.coordinateToLogical(anchorCoordinate);
  const span = Math.max(1e-6, currentRange.to - currentRange.from);
  const fallbackLogical = currentRange.from + clampUnit(anchorCoordinate / plotWidth) * span;
  const anchorLogical = Number(anchorLogicalFromCoord ?? (fallbackLogical as Logical));

  return {
    logical: anchorLogical,
    ratio: clampUnit((anchorLogical - currentRange.from) / span),
    pointX,
    source,
    pointerFromEvent: Number(pointerFromEvent.toFixed(2)),
  };
}

function buildZoomedRange(
  currentRange: { from: number; to: number },
  anchor: { logical: number; ratio: number; pointX: number },
  deltaY: number,
  barCount: number,
) {
  const currentSpan = Math.max(10, currentRange.to - currentRange.from);
  const zoomFactor = deltaY > 0 ? 1.14 : 0.88;
  const nextSpan = Math.max(10, Math.min(barCount + 120, currentSpan * zoomFactor));
  const rawFrom = anchor.logical - anchor.ratio * nextSpan;
  return clampLogicalRange({ from: rawFrom, to: rawFrom + nextSpan }, barCount);
}

function clampUnit(value: number) {
  return Math.min(1, Math.max(0, value));
}

function clampNumber(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function resolveKeyboardZoomAnchor(
  mainRuntime: PaneRuntime,
  currentRange: { from: number; to: number },
  pointerX: number | null,
  plotWidth: number,
) {
  const timeScale = mainRuntime.chart.timeScale();
  const pointXResolved = clampNumber(pointerX ?? plotWidth, 0, plotWidth);
  const span = Math.max(1e-6, currentRange.to - currentRange.from);
  const logical =
    timeScale.coordinateToLogical(pointXResolved) ??
    ((currentRange.from + clampUnit(pointXResolved / plotWidth) * span) as Logical);

  return {
    logical: Number(logical),
    ratio: clampUnit((Number(logical) - currentRange.from) / span),
    pointX: pointXResolved,
    source: pointerX == null ? "right-edge" : "last-pointer",
  };
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  return (
    target.isContentEditable ||
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    Boolean(target.closest("[contenteditable='true']"))
  );
}
